// src/components/PrivacyPolicy.js

import React from 'react';

const PrivacyPolicy = () => {
    return (
        <main className="bg-slate-50 pt-28 md:pt-40 pb-20">
            <div className="max-w-4xl mx-auto px-6">
                
                {/* Page Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full mb-6 shadow-sm">
                        <svg className="w-3 h-3 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>
                        <span className="text-[10px] font-black tracking-[0.2em] text-brand-dark uppercase">Data Protection</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-brand-dark tracking-tighter uppercase">
                        Privacy Policy
                    </h1>
                </div>

                {/* Main Content Wrapper */}
                <div className="space-y-10">
                    <p className="text-slate-700 text-justify leading-loose">
                        Acadfinity (“we”, “us”, “our”) operates the Acadfinity Careers platform and related hiring services (“Service”). This Privacy Policy explains how we collect, use, store, and share personal information submitted by candidates and institutions while using our hiring and talent acquisition services.
                    </p>

                    {/* Section 1 */}
                    <div>
                        <h2 className="text-2xl font-black text-brand-dark mb-4">1. Information We Collect</h2>
                        <p className="text-slate-700 text-justify leading-loose">
                            To facilitate school hiring and talent matching, we may collect the following information:
                        </p>
                        <h3 className="text-xl font-bold text-slate-800 mb-3 mt-6">a) Personal Information</h3>
                        <p className="text-slate-700 text-justify leading-loose mb-3">
                            When you submit your profile or interact with our Service, we may collect:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-slate-700 text-justify leading-loose">
                            <li>Full name</li>
                            <li>Email address</li>
                            <li>Mobile number</li>
                            <li>Resume / CV</li>
                            <li>Educational qualifications</li>
                            <li>Professional experience</li>
                            <li>Preferred role, subject, and location</li>
                        </ul>
                        
                        <h3 className="text-xl font-bold text-slate-800 mb-3 mt-8">b) Usage & Technical Information</h3>
                        <p className="text-slate-700 text-justify leading-loose mb-3">
                           We may automatically collect limited technical data such as:
                        </p>
                         <ul className="list-disc pl-6 space-y-2 text-slate-700 text-justify leading-loose">
                            <li>IP address</li>
                            <li>Browser type and device information</li>
                            <li>Pages visited and interaction patterns</li>
                        </ul>
                        <p className="text-slate-700 text-justify leading-loose mt-4">
                            This data is used only to improve platform performance and user experience.
                        </p>
                    </div>

                    {/* Section 2 */}
                    <div>
                        <h2 className="text-2xl font-black text-brand-dark mb-4">2. How We Use Your Information</h2>
                        <p className="text-slate-700 text-justify leading-loose">
                            Acadfinity uses collected information strictly for:
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-slate-700 text-justify leading-loose">
                            <li>Connecting candidates with verified partner schools</li>
                            <li>Facilitating school-level hiring and interview coordination</li>
                            <li>Maintaining candidate records in the Acadfinity Talent Pool</li>
                            <li>Responding to queries and providing support</li>
                            <li>Improving service quality and recruitment accuracy</li>
                        </ul>
                         <p className="mt-4 p-4 bg-slate-100 rounded-lg text-slate-600 text-sm font-medium leading-relaxed">
                            <strong>Note:</strong> Acadfinity does not sell, rent, or misuse candidate data for marketing or unrelated purposes.
                        </p>
                    </div>

                    {/* Section 3 */}
                    <div>
                        <h2 className="text-2xl font-black text-brand-dark mb-4">3. Sharing & Disclosure of Information</h2>
                        <p className="text-slate-700 text-justify leading-loose">
                           Your information may be shared only in the following cases:
                        </p>
                        <h3 className="text-xl font-bold text-slate-800 mb-3 mt-6">a) With Partner Educational Institutions</h3>
                        <p className="text-slate-700 text-justify leading-loose">
                            Candidate profiles may be shared only with verified schools for recruitment and interview purposes, based on role suitability and consent.
                        </p>
                        <h3 className="text-xl font-bold text-slate-800 mb-3 mt-8">b) Legal & Compliance Requirements</h3>
                         <p className="text-slate-700 text-justify leading-loose">
                            We may disclose information if required to:
                        </p>
                         <ul className="list-disc pl-6 space-y-2 text-slate-700 text-justify leading-loose">
                            <li>Comply with applicable laws or regulations</li>
                            <li>Respond to lawful requests by authorities</li>
                            <li>Protect the rights, safety, or integrity of Acadfinity, users, or institutions</li>
                        </ul>
                    </div>

                    {/* Section 4 */}
                    <div>
                        <h2 className="text-2xl font-black text-brand-dark mb-4">4. Data Security</h2>
                        <p className="text-slate-700 text-justify leading-loose">
                            We take reasonable administrative and technical measures to safeguard personal information. However, no digital platform can guarantee 100% security. By using our Service, you acknowledge this inherent risk.
                        </p>
                    </div>

                    {/* Section 5 */}
                    <div>
                        <h2 className="text-2xl font-black text-brand-dark mb-4">5. Children’s Privacy</h2>
                        <p className="text-slate-700 text-justify leading-loose">
                           Acadfinity’s Careers platform is intended for individuals aged 18 years and above. We do not knowingly collect personal information from minors.
                        </p>
                    </div>

                    {/* Section 6 */}
                    <div>
                        <h2 className="text-2xl font-black text-brand-dark mb-4">6. Policy Updates</h2>
                        <p className="text-slate-700 text-justify leading-loose">
                            This Privacy Policy may be updated periodically. Continued use of the Service after updates constitutes acceptance of the revised policy.
                        </p>
                    </div>
                    
                    {/* Section 7 - Highlighted Contact Box */}
                    <div className="!mt-12 bg-slate-100/70 p-6 rounded-xl">
                        <h2 className="text-2xl font-black text-brand-dark mb-3">7. Contact</h2>
                        <p className="text-slate-700 leading-relaxed">
                            For any privacy-related concerns, please contact:
                            <a href="mailto:careers@acadfinity.com" className="font-semibold text-brand-orange hover:underline ml-2">
                                📧 careers@acadfinity.com
                            </a>
                        </p>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default PrivacyPolicy;